import compose from 'lodash/fp/compose';
import get from 'lodash/get';
import { arrayOf, shape, string } from 'prop-types';
import qs from 'query-string';
import React from 'react';

import { OPPORTUNITY } from '../attachments/constants';
import ResolvedAttachments from '../attachments/ListAttachments';
import BalanceModal from '../balance/BalanceModal';
import { ProvideCategories } from '../generic/CategorySelect';
import { withDocumentTitle } from '../generic/DocumentTitle';
import ScopedDrawer, {
  approval,
  changelog,
  comments,
} from '../generic/ScopedDrawer';
import { getTooltipButtonWithText } from '../generic/Tooltip';
import Titled from '../layout/Titled';
import { isSuperuser } from '../layout/Variant/Variant';
import { detailedOpportunityShape, merchantShape } from '../prop-types';
import AddQuoteButton from '../quotes/AddQuoteButton';
import ListQuotes from '../quotes/ListQuotes';
import api from '../REST/api';
import AuthorizedLink from '../REST/AuthorizedLink';
import withResolved from '../REST/with-resolved';
import AddCampaignButton from './AddCampaignButton';
import CloneOpportunity from './CloneOpportunity';
import { salesChannelsEnum } from './constants';
import HELP_TEXTS from './help-texts';
import OpportunityForm from './OpportunityForm';
import RecreateCampaignButton from './RecreateCampaignButton';
import { transformForRendering } from './transform-opportunity-helpers';

const ButtonWithTooltip = getTooltipButtonWithText(HELP_TEXTS);

class ShowOpportunity extends React.Component {
  constructor(props) {
    super(props);

    this.state = { model: this.props.model };
  }

  static getDerivedStateFromProps(props, state) {
    if (state.model.id === props.model.id) {
      return null;
    }
    return { model: props.model };
  }

  addCampaignIdToModel(campaign) {
    this.setState(prevState => ({
      model: {
        ...prevState.model,
        campaignId: campaign.id,
      },
    }));
  }

  render() {
    const { model } = this.state;
    const { location, merchants } = this.props;
    const opportunityId = model.id;
    const context = { contentType: 'opportunity', objectId: opportunityId };
    const search = qs.parse(location.search);
    const { openInvoiceId, openBalanceModal } = search;
    const isSelfService = model.salesChannel === salesChannelsEnum.SELF_SERVICE;
    const isInvoicingDisabled = !isSelfService && !model.primaryQuote;
    const isSalesforceOpp = !!model.salesforceId;
    const hasReservationId = !!model.reservationId;

    return (
      <ProvideCategories>
        <Titled
          title={`Opportunity - ${model?.id}`}
          subHeader={[
            isSalesforceOpp ? `Salesforce ID - ${model?.salesforceId}` : '',
            hasReservationId ? `Reservation ID - ${model?.reservationId}` : '',
          ]}
          actions={[
            <span className="mr-2">
              <BalanceModal
                opportunity={model}
                openInvoiceId={Number.parseInt(openInvoiceId, 10)}
                isOpenByDefault={openBalanceModal === 'true' || !!openInvoiceId}
                tag={ButtonWithTooltip}
                additionalButtonProps={{
                  helpKey: 'invoicingIsMissingPrimaryQuote',
                  section: 'navigation',
                  showTooltip: isInvoicingDisabled,
                }}
                disabled={isInvoicingDisabled}
              />
            </span>,
            !isSelfService && !isSalesforceOpp && isSuperuser() && (
              <span className="mr-2">
                <CloneOpportunity
                  opportunity={transformForRendering(model, merchants)}
                  merchants={merchants}
                />
              </span>
            ),
            !isSelfService &&
              (model.campaignId ? (
                <RecreateCampaignButton campaignId={model.campaignId} />
              ) : (
                <AddCampaignButton
                  opportunityId={opportunityId}
                  onSuccess={campaign => this.addCampaignIdToModel(campaign)}
                />
              )),
            <AuthorizedLink
              to="edit"
              className="btn btn-secondary ml-2"
              query={api.patchOpportunity}
            >
              Edit
            </AuthorizedLink>,
          ]}
          sticky
        >
          <div className="mb-4">
            <ScopedDrawer
              options={[
                comments(context),
                changelog(context),
                approval(context),
              ]}
            >
              <div className="mb-4">
                <OpportunityForm
                  model={transformForRendering(model, merchants)}
                  isSalesforceOpp={isSalesforceOpp}
                />
              </div>
              <div className="mb-4">
                <ResolvedAttachments
                  objectId={opportunityId}
                  contentType={OPPORTUNITY}
                />
              </div>
              <div className="mb-4">
                <ListQuotes
                  opportunityId={model.id}
                  primaryQuoteId={get(model, 'primaryQuote.id')}
                  actions={
                    !isSelfService &&
                    isSuperuser() &&
                    !isSalesforceOpp && (
                      <AddQuoteButton opportunityId={model.id} />
                    )
                  }
                  showPagination={false}
                />
              </div>
            </ScopedDrawer>
          </div>
        </Titled>
      </ProvideCategories>
    );
  }
}

ShowOpportunity.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  match: shape({ params: shape({ id: string.isRequired }).isRequired })
    .isRequired,
  model: detailedOpportunityShape.isRequired,
  location: shape({ search: string }).isRequired,
  merchants: arrayOf(merchantShape).isRequired,
};

export default compose(
  withResolved({
    query: api.getOpportunity,
    deriveQueryArgs: props => ({ id: props.match.params.id }),
    as: 'model',
  }),
  withDocumentTitle(({ model }) => model.name),
)(ShowOpportunity);
